/* ============================================================
   DogMama Landing Page — shared JS
   All data comes from DOM (schema settings / blocks). No hardcoded content.
   ============================================================ */

(() => {
  'use strict';

  // ---- Product / cart state ----
  const productState = {
    handle: '',
    sellingPlanId: null,
    currency: 'CZK',
    saveText: '',
    loopWidget: false,
  };

  function readProductConfig() {
    const cfg = document.querySelector('[data-dm-lp-config]');
    if (!cfg) return;
    productState.handle = cfg.dataset.productHandle || '';
    const plan = Number(cfg.dataset.sellingPlanId);
    productState.sellingPlanId = plan || null;
    productState.currency = cfg.dataset.currency || 'CZK';
    productState.saveText = cfg.dataset.labelSave || '';
    productState.loopWidget = cfg.hasAttribute('data-loop-widget');
  }

  function formatMoney(cents, currencyCode) {
    // Always Czech format (e.g. "999,00 Kč"), matching the PDP / Loop widget prices.
    const amount = parseFloat(cents) / 100;
    try {
      return new Intl.NumberFormat('cs-CZ', {
        style: 'currency',
        currency: currencyCode || 'CZK',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      const fixed = amount.toFixed(2).replace('.', ',');
      return fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' Kč';
    }
  }

  function updatePrice(priceCents, compareCents, currency) {
    const elNew = document.getElementById('bundle-price-new');
    const elOld = document.getElementById('bundle-price-old');
    const elSave = document.getElementById('bundle-price-save');
    if (elNew) elNew.textContent = formatMoney(priceCents, currency);
    if (elOld) elOld.textContent = formatMoney(compareCents, currency);
    if (elSave && productState.saveText) elSave.textContent = productState.saveText;
    // One-time purchase (Loop widget) has no discount — hide strike-through + save text.
    const discounted = Number(compareCents) > Number(priceCents);
    if (elOld) elOld.hidden = !discounted;
    if (elSave) elSave.hidden = !discounted;
  }

  // Fetched once and reused, so switching plans updates the price instantly.
  let productPromise = null;
  function getProduct() {
    if (!productPromise) {
      productPromise = fetch('/products/' + productState.handle + '.js', {
        headers: { Accept: 'application/json' },
      }).then((res) => (res.ok ? res.json() : null));
      productPromise.catch(() => { productPromise = null; });
    }
    return productPromise;
  }

  async function loadLivePrice() {
    if (!productState.handle) return;
    try {
      const product = await getProduct();
      if (!product || !product.variants || !product.variants.length) return;

      const variant = product.variants.find((v) => v.available) || product.variants[0];
      if (!variant) return;

      productState.currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        productState.currency;

      let price = variant.price;
      let compare = variant.compare_at_price || variant.price;
      if (productState.sellingPlanId) {
        const allocation = (variant.selling_plan_allocations || []).find(
          (item) => Number(item.selling_plan_id) === productState.sellingPlanId
        );
        if (allocation) {
          price = Number(allocation.price);
          compare = Number(allocation.compare_at_price || variant.price);
        }
      }
      updatePrice(price, compare, productState.currency);
    } catch (err) {
      console.warn('[dm-lp] price:', err && err.message);
    }
  }

  // ---- Add to cart ----
  // The hero uses the theme's native <product-form> element for the actual submit.
  // Every other .pbb-atc-btn on the page just scrolls to the hero form and
  // triggers its submit, so cart drawer opening / errors / translations all
  // flow through the theme's own components — no custom fetch/toast needed.
  function findHeroForm() {
    return document.querySelector('form[id^="dm-lp-hero-form-"]');
  }
  function scrollToHeroAndSubmit() {
    const form = findHeroForm();
    if (!form) return;
    const btn = form.querySelector('button[type="submit"]');
    const target = form.closest('.pbb-hero') || form;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Small delay so the scroll starts before the drawer opens
    setTimeout(() => {
      if (btn && !btn.disabled) btn.click();
    }, 350);
  }

  // ---- Reveal animations ----
  // In the Shopify theme editor (design mode), IntersectionObserver is unreliable
  // because the preview iframe doesn't scroll normally — so skip the hide/reveal
  // dance entirely and leave everything visible.
  function initAnimations() {
    const inDesignMode = !!(window.Shopify && window.Shopify.designMode);
    const wrappers = document.querySelectorAll('.pbb-lp');
    if (inDesignMode || !('IntersectionObserver' in window)) {
      return; // Content is already visible by default (CSS opts in only when data-anim="on").
    }
    // Mark elements already in viewport as visible BEFORE enabling data-anim,
    // so they don't flicker from visible → hidden → visible when JS boots.
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const els = document.querySelectorAll('.pbb-anim');
    els.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < viewportH * 0.9 && rect.bottom > 0) {
        el.classList.add('pbb-visible');
      }
    });
    wrappers.forEach((w) => w.setAttribute('data-anim', 'on'));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('pbb-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => { if (!el.classList.contains('pbb-visible')) observer.observe(el); });
  }

  // ---- Ingredients tabs (data-driven from blocks) ----
  function initIngredients() {
    document.querySelectorAll('[data-dm-lp-ingredients]').forEach((root) => {
      const items = [...root.querySelectorAll('[data-dm-lp-ingredient]')];
      if (!items.length) return;
      const nav = root.querySelector('[data-dm-lp-ingredients-nav]');
      const panel = root.querySelector('[data-dm-lp-ingredient-panel]');
      if (!nav || !panel) return;

      const parts = {
        media: panel.querySelector('[data-field="media"]'),
        img: panel.querySelector('[data-field="image"]'),
        avatar: panel.querySelector('[data-field="avatar"]'),
        name: panel.querySelector('[data-field="name"]'),
        amount: panel.querySelector('[data-field="amount"]'),
        copy: panel.querySelector('[data-field="copy"]'),
        points: panel.querySelector('[data-field="points"]'),
        research: panel.querySelector('[data-field="research"]'),
        sources: panel.querySelector('[data-field="sources"]'),
      };
      const sourcesLabel = panel.dataset.sourcesLabel || 'Sources';

      function render(index) {
        const src = items[index];
        if (!src) return;
        if (parts.img && parts.media) {
          const imgSrc = src.dataset.image || '';
          if (imgSrc) {
            // Same image already loaded → skip loader
            if (parts.img.src === imgSrc && parts.img.complete && parts.img.naturalWidth > 0) {
              parts.media.hidden = false;
            } else {
              parts.media.hidden = false;
              parts.media.setAttribute('data-loading', 'true');
              const onDone = () => {
                parts.media.removeAttribute('data-loading');
                parts.img.removeEventListener('load', onDone);
                parts.img.removeEventListener('error', onDone);
              };
              parts.img.addEventListener('load', onDone);
              parts.img.addEventListener('error', onDone);
              parts.img.src = imgSrc;
              parts.img.alt = src.dataset.imageAlt || '';
              // Cached images may not fire load — check synchronously
              if (parts.img.complete && parts.img.naturalWidth > 0) onDone();
            }
          } else {
            parts.media.hidden = true;
            parts.media.removeAttribute('data-loading');
          }
        }
        if (parts.name) {
          const rawName = src.dataset.name || '';
          const dashIdx = rawName.indexOf('—');
          if (dashIdx > -1) {
            const head = rawName.slice(0, dashIdx).replace(/\s+$/, '');
            const tail = rawName.slice(dashIdx + 1).replace(/^\s+/, '');
            parts.name.replaceChildren();
            parts.name.append(head);
            const div = document.createElement('span');
            div.className = 'devider';
            div.textContent = ' —';
            parts.name.append(div);
            const sub = document.createElement('span');
            sub.className = 'sub-part';
            sub.textContent = ' ' + tail;
            parts.name.append(sub);
          } else {
            parts.name.textContent = rawName;
          }
        }
        if (parts.amount) {
          parts.amount.textContent = src.dataset.amount || '';
          parts.amount.hidden = !src.dataset.amount;
        }
        if (parts.avatar) {
          const avatarSrc = src.dataset.image || '';
          if (avatarSrc) {
            parts.avatar.style.backgroundImage = "url(" + avatarSrc + ")";
            parts.avatar.removeAttribute('hidden');
          } else {
            parts.avatar.style.backgroundImage = '';
          }
        }
        if (parts.copy) parts.copy.innerHTML = src.dataset.copy || '';
        if (parts.research) parts.research.textContent = src.dataset.research || '';
        if (parts.sources) {
          parts.sources.textContent = src.dataset.sources
            ? sourcesLabel + ' ' + src.dataset.sources
            : '';
        }
        if (parts.points) {
          const raw = src.dataset.points || '';
          const pts = raw.split('|').map((s) => s.trim()).filter(Boolean);
          parts.points.replaceChildren(
            ...pts.map((p) => {
              const li = document.createElement('li');
              li.textContent = p;
              return li;
            })
          );
        }
        nav.querySelectorAll('.pbb-ingredient-tab').forEach((tab, i) => {
          const active = i === index;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
        });
      }

      items.forEach((item, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pbb-ingredient-tab';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        button.tabIndex = index === 0 ? 0 : -1;
        button.textContent = item.dataset.short || item.dataset.name || '';
        button.addEventListener('click', () => render(index));
        nav.appendChild(button);
      });

      nav.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        const tabs = [...nav.querySelectorAll('.pbb-ingredient-tab')];
        const current = tabs.indexOf(document.activeElement);
        if (current < 0) return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const next = (current + direction + tabs.length) % tabs.length;
        tabs[next].focus();
        render(next);
      });

      render(0);
    });
  }

  // ---- Source references list ----
  function initSources() {
    document.querySelectorAll('[data-dm-lp-sources]').forEach((list) => {
      const items = [...list.querySelectorAll('[data-dm-lp-source]')];
      if (!items.length) return;
      const ordered = items
        .map((el) => el.textContent.trim())
        .filter(Boolean);
      list.replaceChildren(
        ...ordered.map((ref) => {
          const li = document.createElement('li');
          li.textContent = ref;
          return li;
        })
      );
    });
  }

  // ---- How-it-works tabs ----
  function initHowTabs() {
    document.querySelectorAll('[data-dm-lp-how]').forEach((root) => {
      const tablist = root.querySelector('[role="tablist"]');
      if (!tablist) return;
      const tabs = [...tablist.querySelectorAll('[role="tab"]')];
      const panels = tabs.map((tab) => root.querySelector('#' + tab.getAttribute('aria-controls')));
      const number = root.querySelector('[data-dm-lp-how-number]');
      const media = root.querySelector('[data-dm-lp-how-media]');
      const image = root.querySelector('[data-dm-lp-how-image]');

      function swapImage(tab) {
        if (!media || !image) return;
        const src = tab.dataset.stepImage || '';
        const alt = tab.dataset.stepImageAlt || '';
        if (!src) return;
        if (image.src === src && image.complete && image.naturalWidth > 0) return;
        media.setAttribute('data-loading', 'true');
        const onDone = () => {
          media.removeAttribute('data-loading');
          image.removeEventListener('load', onDone);
          image.removeEventListener('error', onDone);
        };
        image.addEventListener('load', onDone);
        image.addEventListener('error', onDone);
        image.src = src;
        image.alt = alt;
        if (image.complete && image.naturalWidth > 0) onDone();
      }

      function activate(index, moveFocus) {
        tabs.forEach((tab, i) => {
          const active = i === index;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          if (panels[i]) panels[i].hidden = !active;
        });
        if (number) number.textContent = String(index + 1);
        if (tabs[index]) swapImage(tabs[index]);
        if (moveFocus) tabs[index].focus();
      }

      tabs.forEach((tab, i) => tab.addEventListener('click', () => activate(i, false)));
      tablist.addEventListener('keydown', (event) => {
        const current = tabs.indexOf(document.activeElement);
        if (current < 0) return;
        let next = current;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(next, true);
      });
    });
  }

  // ---- Video cards (play + mute) ----
  // Videos autoplay muted+loop on load. Play button toggles play/pause,
  // sound button toggles mute. Everything stays visible always.
  // For YouTube/Vimeo URLs, we inject an iframe with autoplay=1&mute=1 on load
  // (play/sound buttons interact via postMessage where supported).
  function buildEmbedUrl(rawUrl) {
    try {
      const url = new URL(rawUrl);
      const host = url.hostname;
      if (host.includes('youtube.com') || host.includes('youtu.be')) {
        let id = '';
        if (host.includes('youtu.be')) id = url.pathname.slice(1);
        else if (url.pathname.startsWith('/watch')) id = url.searchParams.get('v') || '';
        else if (url.pathname.startsWith('/embed/')) id = url.pathname.split('/').pop();
        if (!id) return '';
        return (
          'https://www.youtube.com/embed/' + id +
          '?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&rel=0&playsinline=1&enablejsapi=1&playlist=' + id
        );
      }
      if (host.includes('vimeo.com')) {
        const id = url.pathname.split('/').filter(Boolean).pop();
        if (!id) return '';
        return 'https://player.vimeo.com/video/' + id + '?autoplay=1&muted=1&loop=1&background=0&controls=0';
      }
    } catch (e) { /* invalid URL */ }
    return '';
  }

  function ytCommand(iframe, func) {
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: func, args: [] }), '*');
  }
  function vimeoCommand(iframe, method) {
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(JSON.stringify({ method: method }), '*');
  }
  function iframePlatform(iframe) {
    if (!iframe) return null;
    const src = iframe.src || '';
    if (src.includes('youtube.com')) return 'yt';
    if (src.includes('player.vimeo.com')) return 'vimeo';
    return null;
  }

  function initVideoCards() {
    const cardApis = [];

    document.querySelectorAll('[data-dm-lp-video-card]').forEach((card) => {
      const videoEl = card.querySelector('[data-dm-lp-video]');
      const embedWrap = card.querySelector('[data-dm-lp-video-embed]');
      const playBtn = card.querySelector('[data-dm-lp-video-play]');
      const playIcon = playBtn && playBtn.querySelector('.pbb-video-play-icon');
      const pauseIcon = playBtn && playBtn.querySelector('.pbb-video-pause-icon');
      const soundBtn = card.querySelector('[data-dm-lp-video-sound]');
      const soundOn = soundBtn && soundBtn.querySelector('.pbb-video-sound-on');
      const soundOff = soundBtn && soundBtn.querySelector('.pbb-video-sound-off');

      let embedIframe = null;
      let embedPlaying = true;
      let embedMuted = true;

      function setPlayIcon(playing) {
        if (!playBtn) return;
        playBtn.setAttribute('data-playing', playing ? 'true' : 'false');
        card.setAttribute('data-playing', playing ? 'true' : 'false');
        if (playIcon) playIcon.toggleAttribute('hidden', playing);
        if (pauseIcon) pauseIcon.toggleAttribute('hidden', !playing);
      }

      function setSoundIcon(muted) {
        if (!soundBtn) return;
        soundBtn.setAttribute('data-muted', muted ? 'true' : 'false');
        // sound-off SVG = "currently muted" glyph (X over speaker) → show when muted
        // sound-on  SVG = "currently playing sound" glyph → show when unmuted
        if (soundOff) soundOff.toggleAttribute('hidden', !muted);
        if (soundOn) soundOn.toggleAttribute('hidden', muted);
      }

      // -- Autoplay setup for URL embeds ---------------------------------
      if (embedWrap && embedWrap.dataset.autoplay === 'true') {
        const embed = buildEmbedUrl(embedWrap.dataset.embedUrl);
        if (embed) {
          embedIframe = document.createElement('iframe');
          embedIframe.src = embed;
          embedIframe.setAttribute('frameborder', '0');
          embedIframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
          embedIframe.setAttribute('allowfullscreen', '');
          embedWrap.innerHTML = '';
          embedWrap.appendChild(embedIframe);
        }
      }

      // Initial UI state — muted, and treated as "playing" (icons show pause + click-to-unmute)
      setPlayIcon(!!videoEl || !!embedIframe);
      setSoundIcon(true);

      // -- Lazy-load native video sources + autoplay when in viewport ----
      if (videoEl && videoEl.hasAttribute('data-dm-lp-lazy-video')) {
        let sourcesLoaded = false;
        const activateSources = () => {
          if (sourcesLoaded) return;
          sourcesLoaded = true;
          videoEl.querySelectorAll('source[data-src]').forEach((s) => {
            s.setAttribute('src', s.getAttribute('data-src'));
            s.removeAttribute('data-src');
          });
          videoEl.load();
        };
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting) {
                activateSources();
                videoEl.play().catch(() => {});
              } else if (!videoEl.paused) {
                videoEl.pause();
              }
            });
          }, { threshold: 0.25 });
          io.observe(videoEl);
        } else {
          activateSources();
          videoEl.play().catch(() => {});
        }
      }

      // -- Play button: toggles play/pause -------------------------------
      if (playBtn) {
        playBtn.addEventListener('click', function () {
          if (videoEl) {
            if (videoEl.paused) {
              videoEl.play().catch(() => {});
            } else {
              videoEl.pause();
            }
          } else if (embedIframe) {
            const p = iframePlatform(embedIframe);
            embedPlaying = !embedPlaying;
            if (p === 'yt') ytCommand(embedIframe, embedPlaying ? 'playVideo' : 'pauseVideo');
            else if (p === 'vimeo') vimeoCommand(embedIframe, embedPlaying ? 'play' : 'pause');
            setPlayIcon(embedPlaying);
          }
        });
      }

      function muteCard() {
        if (videoEl) {
          videoEl.muted = true;
        } else if (embedIframe) {
          const p = iframePlatform(embedIframe);
          if (p === 'yt') ytCommand(embedIframe, 'mute');
          else if (p === 'vimeo') vimeoCommand(embedIframe, 'setMuted');
          embedMuted = true;
        }
        setSoundIcon(true);
      }

      // -- Sound button: toggles mute (single-unmuted enforcement) -------
      if (soundBtn) {
        soundBtn.addEventListener('click', function () {
          const willUnmute = videoEl ? videoEl.muted : embedMuted;
          if (willUnmute) {
            // Mute every other card first
            cardApis.forEach((api) => { if (api.card !== card) api.mute(); });
          }
          if (videoEl) {
            videoEl.muted = !videoEl.muted;
            setSoundIcon(videoEl.muted);
          } else if (embedIframe) {
            const p = iframePlatform(embedIframe);
            embedMuted = !embedMuted;
            if (p === 'yt') ytCommand(embedIframe, embedMuted ? 'mute' : 'unMute');
            else if (p === 'vimeo') vimeoCommand(embedIframe, 'setMuted');
            setSoundIcon(embedMuted);
          }
        });
      }

      // -- Sync native video state with icons ----------------------------
      if (videoEl) {
        videoEl.addEventListener('play', function () { setPlayIcon(true); });
        videoEl.addEventListener('playing', function () { setPlayIcon(true); });
        videoEl.addEventListener('pause', function () { setPlayIcon(false); });
        videoEl.addEventListener('volumechange', function () { setSoundIcon(videoEl.muted); });
      }

      cardApis.push({ card: card, mute: muteCard });
    });
  }

  // ---- Plan selector ----
  // Updates the hidden selling_plan input on the hero form AND refreshes the
  // live price. The form submit is handled by the theme's <product-form>.
  function initPlanSelect() {
    document.querySelectorAll('[data-dm-lp-plan-select]').forEach((planSelect) => {
      planSelect.addEventListener('change', () => {
        const selected = planSelect.value;
        productState.sellingPlanId = Number(selected) || null;
        const heroForm = findHeroForm();
        if (heroForm) {
          const planInput = heroForm.querySelector('[data-dm-lp-selling-plan-input]');
          if (planInput) planInput.value = selected || '';
        }
        loadLivePrice();
      });
    });
  }

  // ---- Loop widget (PDP purchase options) ----
  // Loop writes the chosen plan into its own hidden selling_plan input inside
  // the hero form (empty = one-time). Mirror it into the hero price row.
  function initLoopWidgetSync() {
    if (!productState.loopWidget) return;
    const heroForm = findHeroForm();
    if (!heroForm) return;

    // Hero price is server-rendered for Loop's default (one-time = no plan),
    // so only re-price when the shopper actually picks something else.
    let lastPlan = null;
    function sync() {
      const input = heroForm.querySelector('input[name="selling_plan"]');
      const plan = (input && Number(input.value)) || null;
      if (plan === lastPlan) return;
      lastPlan = plan;
      productState.sellingPlanId = plan;
      loadLivePrice();
    }

    heroForm.addEventListener('change', () => setTimeout(sync, 50));
    // Loop applies its default selection asynchronously after init.
    [600, 1500, 3000].forEach((ms) => setTimeout(sync, ms));
  }

  // ---- Inline CTAs → scroll to hero form and submit ----
  function initInlineCTAs() {
    document.querySelectorAll('.pbb-atc-btn').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        scrollToHeroAndSubmit();
      });
    });
  }

  // ---- Hero rating → scroll to Judge.me widget ----
  function initJmScroll() {
    document.querySelectorAll('[data-dm-lp-jm-scroll]').forEach((el) => {
      el.addEventListener('click', function (e) {
        const target = document.getElementById('judgeme_product_reviews')
          || document.querySelector('[id^="judgeme_product_reviews"]')
          || document.querySelector('.jdgm-review-widget, .jdgm-widget');
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  // ---- Init ----
  function init() {
    readProductConfig();
    initAnimations();
    initIngredients();
    initSources();
    initHowTabs();
    initPlanSelect();
    initLoopWidgetSync();
    initVideoCards();
    initInlineCTAs();
    initJmScroll();
    // No initial loadLivePrice(): the hero price row is already rendered in Liquid.

    document.querySelectorAll('.pbb-hero .pbb-anim').forEach((el, i) => {
      setTimeout(() => el.classList.add('pbb-visible'), 80 + i * 80);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
